/* ===
   dip dental™ — mobile-only "Add to Home Screen" install popup.

   Vanilla JS, no dependencies. This is the single owner of install
   behaviour site-wide: it drives both the popup and the footer notice's
   button. (A previous assets/pwa-install.js duplicated this logic against
   DOM hooks that no longer existed; it has been deleted.)

   Three install paths, chosen automatically per browser:
   - Android/Chrome WITH a captured native prompt: tapping the CTA replays
     Chrome's own install dialog.
   - Android/Chrome WITHOUT a native prompt available (Chrome requires a
     registered service worker with a fetch handler for the automatic
     `beforeinstallprompt` event — this site doesn't have one yet): falls
     back to a short "here's how" sheet pointing at Chrome's menu, same
     spirit as the iOS sheet. Once a service worker is added, this path
     naturally stops being used — canOffer() prefers deferredPrompt first.
   - iOS Safari: always the manual sheet — Apple has no install API at all.

   Safe to delete this file together with snippets/pwa-install-popup.liquid,
   assets/pwa-install-popup-v2.css, and its render line in layout/theme.liquid
   to remove the popup. The footer notice is a separate, unaffected component.

   build: 2026-08-20f
   === */
(function () {
  'use strict';

  var DISMISS_KEY = 'dd_pwa_popup_dismissed'; // timestamp => dismissed until it expires
  var INSTALLED_KEY = 'dd_pwa_popup_installed'; // set => already installed
  var SHOW_DELAY_MS = 45000;

  // Adding ?pwa_test=1 to any URL cuts the wait to 2 seconds. Invisible to
  // visitors and changes nothing else — it exists because verifying this
  // feature otherwise means waiting 45 seconds without letting the screen
  // dim, which is a miserable way to check a change.
  try {
    if (window.location.search.indexOf('pwa_test=1') !== -1) SHOW_DELAY_MS = 2000;
  } catch (e) {
    /* ignore */
  }
  var DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // re-offer 30 days after a dismissal
  var CONFLICT_RECHECK_MS = 1000;
  var CONFLICT_CLEAR_DELAY_MS = 800;
  // Hard ceiling on how long we'll wait for a conflicting modal to close. The
  // conflict check is a heuristic over markup we don't control, so it can be
  // wrong in ways we can't foresee; without this cap a false positive would
  // block the popup silently and permanently. After this we just show.
  var CONFLICT_MAX_WAIT_MS = 6000;
  var MOBILE_QUERY = '(max-width: 767px)';

  var popup = document.getElementById('pwa-popup');
  var iosSheet = document.getElementById('pwa-ios-sheet');
  var androidSheet = document.getElementById('pwa-android-sheet');
  // The footer notice's button (snippets/pwa-home-screen.liquid). This file is
  // the single owner of install behaviour site-wide: two scripts both listening
  // for `beforeinstallprompt` would fight over the one event the browser fires.
  var homeCta = document.querySelector('[data-pwa-home-cta]');
  if (!popup && !homeCta) return;

  var card = popup ? popup.querySelector('.pwa-popup__card') : null;
  var installBtn = popup ? popup.querySelector('[data-pwa-popup-install]') : null;
  var closeBtn = popup ? popup.querySelector('[data-pwa-popup-close]') : null;

  function isMobile() {
    return !!(window.matchMedia && window.matchMedia(MOBILE_QUERY).matches);
  }

  function isStandalone() {
    return (
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator.standalone === true
    );
  }

  function readFlag(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeFlag(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch (e) {
      /* localStorage unavailable (private mode, quota, etc.) — fail silently;
         the popup just won't remember state across visits in that case. */
    }
  }

  function clearFlag(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }

  function writeTimestamp(key) {
    try {
      window.localStorage.setItem(key, String(Date.now()));
    } catch (e) {
      /* ignore — see writeFlag */
    }
  }

  // A dismissal is remembered for DISMISS_TTL_MS, not forever: someone who
  // waved the popup away in January should be asked again months later, and
  // more importantly a permanent flag is unrecoverable — if it is ever written
  // by mistake the popup can never appear again on that device.
  //
  // Older builds stored a literal '1' here with no expiry. Those legacy values
  // are cleared on sight: we can't tell when they were written, and keeping
  // them would mean honouring a dismissal of unknown (possibly infinite) age.
  function dismissedRecently() {
    var raw;
    try {
      raw = window.localStorage.getItem(DISMISS_KEY);
    } catch (e) {
      return false;
    }
    if (!raw) return false;
    if (raw === '1') {
      clearFlag(DISMISS_KEY);
      return false;
    }
    var ts = parseInt(raw, 10);
    if (!ts || isNaN(ts)) {
      clearFlag(DISMISS_KEY);
      return false;
    }
    if (Date.now() - ts >= DISMISS_TTL_MS) {
      clearFlag(DISMISS_KEY);
      return false;
    }
    return true;
  }

  var standalone = isStandalone();
  var installedFlag = readFlag(INSTALLED_KEY);
  var dismissFlag = dismissedRecently();

  // Self-heal a stale INSTALLED_KEY: this flag should only ever be set
  // permanently by a real 'appinstalled' event (below). If it's set but the
  // page isn't currently standalone, it's stale — clear it, otherwise an
  // uninstall would leave the popup silently blocked forever afterwards.
  if (installedFlag && !standalone) {
    clearFlag(INSTALLED_KEY);
    installedFlag = false;
  }

  // Already running as an installed app: there is nothing left to offer
  // anywhere, so hide the footer button too and stop.
  //
  // Note this does NOT permanently write INSTALLED_KEY just from
  // isStandalone() — only the real 'appinstalled' event below does that, so a
  // later uninstall can't leave a stale flag behind.
  if (standalone) {
    if (homeCta) homeCta.hidden = true;
    return;
  }

  // Everything past this point stays wired up regardless of the flags above.
  // Only the automatic popup is suppressed by a previous dismissal — the
  // footer button is something the visitor deliberately reached for, and must
  // keep working even for someone who waved the popup away earlier.
  var autoShowAllowed = !installedFlag && !dismissFlag && isMobile();

  var ua = window.navigator.userAgent || '';
  var isIOSDevice =
    /iP(hone|od|ad)/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  var isSafariBrowser = /^((?!chrome|android|crios|fxios|edgios|opios).)*safari/i.test(ua);
  var isIOSSafari = isIOSDevice && isSafariBrowser;
  var isAndroidDevice = /Android/.test(ua);

  var deferredPrompt = null;
  var lastFocusedEl = null;
  var timerElapsed = false;
  var attempted = false;
  var activeSheet = null;
  var activeSheetSource = null;

  window.addEventListener('beforeinstallprompt', function (event) {
    event.preventDefault();
    deferredPrompt = event;
    // If the delay already elapsed before Chrome fired this (timing is not
    // guaranteed), we now have a real action to offer — try showing right away.
    if (timerElapsed) attemptShow();
  });

  window.addEventListener('appinstalled', function () {
    writeFlag(INSTALLED_KEY);
    if (homeCta) homeCta.hidden = true;
    hidePopup();
    hideSheet();
  });

  // --- Conflict detection: never show on top of another open popup/modal ---
  function anotherPopupIsOpen() {
    var selectors = [
      '.modal[aria-hidden="false"]',
      '.modal--active',
      'dialog[open]',
      '[aria-modal="true"]:not(#pwa-popup):not(#pwa-popup *):not(#pwa-ios-sheet):not(#pwa-ios-sheet *):not(#pwa-android-sheet):not(#pwa-android-sheet *)',
      'privacy-banner[aria-hidden="false"]',
      '.cookie-bar--visible',
      '.cookie-bar.active'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var els;
      try {
        els = document.querySelectorAll(selectors[i]);
      } catch (e) {
        continue;
      }
      for (var j = 0; j < els.length; j++) {
        if (els[j].getClientRects().length > 0) return true;
      }
    }
    // Many popup/app scripts (theme and third-party alike) lock page scroll
    // while a modal is open — a reasonably reliable generic signal even when
    // the exact modal markup isn't known ahead of time (e.g. third-party
    // popup apps installed later).
    //
    // Only the VERTICAL axis counts. This theme sets `overflow-x: hidden` on
    // <html> permanently (a common trick to kill horizontal scroll on mobile),
    // which has nothing to do with a modal being open. Reading the `overflow`
    // shorthand here was the bug: browsers that serialise it as a single value
    // return "hidden" for this theme on every page load, so this function
    // answered "yes, a modal is open" forever and the popup never appeared.
    var htmlOverflowY = window.getComputedStyle(document.documentElement).overflowY;
    var bodyOverflowY = window.getComputedStyle(document.body).overflowY;
    return htmlOverflowY === 'hidden' || bodyOverflowY === 'hidden';
  }

  function whenNoConflict(callback) {
    if (!anotherPopupIsOpen()) {
      callback();
      return;
    }
    var waited = 0;
    var check = setInterval(function () {
      waited += CONFLICT_RECHECK_MS;
      if (!anotherPopupIsOpen()) {
        clearInterval(check);
        setTimeout(callback, CONFLICT_CLEAR_DELAY_MS);
      } else if (waited >= CONFLICT_MAX_WAIT_MS) {
        // Gave the other modal a fair chance; assume the signal was a false
        // positive rather than never showing at all.
        clearInterval(check);
        callback();
      }
    }, CONFLICT_RECHECK_MS);
  }

  // --- Show / hide ---
  function canOffer() {
    // Only worth showing if there is an actual actionable step for this
    // browser: Android/Chrome (native prompt if available, manual menu steps
    // otherwise) or iOS Safari (manual sheet, its only option). Otherwise
    // there's nothing useful to do, so don't show a dead-end button on
    // browsers we don't have a real path for.
    return !!deferredPrompt || isIOSSafari || isAndroidDevice;
  }

  function attemptShow() {
    if (attempted) return;
    if (!canOffer()) return;
    attempted = true;
    whenNoConflict(showPopup);
  }

  function showPopup() {
    if (!popup) return;
    if (readFlag(INSTALLED_KEY) || dismissedRecently()) return;
    if (isStandalone()) return;
    if (!canOffer()) return;
    popup.setAttribute('aria-hidden', 'false');
    popup.classList.add('is-visible');
    if (card) {
      lastFocusedEl = document.activeElement;
      card.focus({ preventScroll: true });
    }
  }

  function hidePopup() {
    if (!popup) return;
    popup.setAttribute('aria-hidden', 'true');
    popup.classList.remove('is-visible');
  }

  function dismissPopup() {
    writeTimestamp(DISMISS_KEY);
    hidePopup();
  }

  // `source` records which control opened the sheet — 'popup' or 'footer'.
  // Closing it means very different things in each case; see the sheet-close
  // handler below.
  function showSheet(sheet, source) {
    if (!sheet) return;
    activeSheet = sheet;
    activeSheetSource = source || null;
    sheet.setAttribute('aria-hidden', 'false');
    sheet.classList.add('is-visible');
    var panel = sheet.querySelector('.pwa-sheet__panel');
    if (panel) {
      lastFocusedEl = document.activeElement;
      panel.focus({ preventScroll: true });
    }
    document.addEventListener('keydown', onSheetKeydown);
  }

  function hideSheet() {
    if (!activeSheet) return;
    activeSheet.setAttribute('aria-hidden', 'true');
    activeSheet.classList.remove('is-visible');
    document.removeEventListener('keydown', onSheetKeydown);
    if (lastFocusedEl && lastFocusedEl.focus) lastFocusedEl.focus({ preventScroll: true });
    activeSheet = null;
    activeSheetSource = null;
  }

  // Escape closes the sheet; Tab is trapped on its one focusable control
  // (the close button) since the numbered steps aren't interactive.
  function onSheetKeydown(event) {
    if (!activeSheet) return;
    var closeEls = activeSheet.querySelectorAll('[data-pwa-sheet-close]');
    if (event.key === 'Escape') {
      hideSheet();
    } else if (event.key === 'Tab' && closeEls.length) {
      event.preventDefault();
      closeEls[0].focus();
    }
  }

  // --- The one install action, shared by every entry point ---
  // Used by the popup's CTA and by the footer notice's button, so both behave
  // identically: replay the browser's own prompt when we have one, otherwise
  // fall back to the manual instructions for that platform.
  function triggerInstall(source) {
    if (deferredPrompt) {
      var promptEvent = deferredPrompt;
      deferredPrompt = null;
      promptEvent.prompt();
      promptEvent.userChoice.then(function (choice) {
        if (choice && choice.outcome === 'accepted') {
          writeFlag(INSTALLED_KEY);
          if (homeCta) homeCta.hidden = true;
        } else {
          // Declining the browser's own install dialog is a clear "no thanks",
          // wherever it was triggered from — don't re-offer for a while.
          writeTimestamp(DISMISS_KEY);
        }
        hidePopup();
      });
    } else if (isIOSSafari) {
      hidePopup();
      showSheet(iosSheet, source);
    } else if (isAndroidDevice) {
      hidePopup();
      showSheet(androidSheet, source);
    }
  }

  // --- Wire up interactions ---
  if (installBtn) {
    installBtn.addEventListener('click', function () {
      triggerInstall('popup');
    });
  }

  if (homeCta) {
    homeCta.addEventListener('click', function () {
      triggerInstall('footer');
    });
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', dismissPopup);
  }

  document.addEventListener('click', function (event) {
    var closeTrigger = event.target.closest && event.target.closest('[data-pwa-sheet-close]');
    if (!closeTrigger) return;

    // Only a sheet the POPUP opened counts as dismissing the popup: the
    // visitor was shown an unsolicited card, followed it, and backed out.
    //
    // A sheet opened from the footer button must NOT dismiss anything. That
    // button is always present, and someone tapping it out of curiosity — then
    // closing the instructions — was never shown the popup at all. Treating
    // that as a dismissal silently suppressed the popup for 30 days for anyone
    // who touched the footer button, which is exactly what it did.
    var openedFromPopup = activeSheetSource === 'popup';
    hideSheet();
    if (openedFromPopup) dismissPopup();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && popup && popup.classList.contains('is-visible')) {
      dismissPopup();
    }
  });

  // --- Timer: SHOW_DELAY_MS after this script runs (i.e. after page load) --
  // Only the automatic popup is on a timer. The footer button, wired above,
  // is always live.
  //
  // setTimeout alone is not enough on a phone. Mobile browsers throttle and
  // then freeze timers in tabs that aren't in the foreground, so if the screen
  // dims or the visitor glances at another app mid-wait, the countdown simply
  // stops — the popup then arrives far later than intended, or not at all.
  //
  // So the timer is treated as a hint, not the source of truth: the decision
  // is made against wall-clock time, which a frozen tab cannot distort. When
  // the page becomes visible again we check how much real time has actually
  // passed and show immediately if the delay is already up.
  if (autoShowAllowed) {
    var startedAt = Date.now();

    var maybeShow = function () {
      if (Date.now() - startedAt < SHOW_DELAY_MS) return;
      timerElapsed = true;
      if (!isMobile()) return;
      attemptShow();
    };

    setTimeout(maybeShow, SHOW_DELAY_MS);

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) maybeShow();
    });
  }
})();
